import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  computed,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { debounceTime, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { MarketingMaterialInventoryService } from 'src/app/services/marketing-material-inventory.service';
import {
  CustomFieldDefinitionsService,
  CustomFieldDefinition,
} from 'src/app/services/custom-field-definitions.service';
import { DeleteConfirmationDialogService } from 'src/app/services/delete-confirmation-dialog.service';
import { ToastService } from 'src/app/services/toast.service';
import { UsersService } from 'src/app/services/users.service';
import { Role } from 'src/app/models/user/role.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { AgGridWrapperComponent } from '../../../components/ag-grid-wrapper/ag-grid-wrapper.component';
import { SearchSortComponent } from '../../../components/search-sort/search-sort.component';
import { BtnCellRenderer } from '../../../components/btn-cell-renderer/btn-cell-renderer.component';
import { TextTooltipRenderer } from '../../../components/text-tooltip-cell-renderer/text-tooltip-cell-renderer.component';
import { baseColDef } from '../../../utils';
import { FormStatusCellRenderer } from '../../../components/form-status/form-status-cell-renderer.component';
import { MultiBadgeCellRenderer } from '../../../components/multi-badge-cell-renderer/multi-badge-cell-renderer.component';
import { GridDocumentComponent } from '../../../components/grid-document/grid-document.component';
import { CustomColumnHeaderComponent } from '../../../components/custom-column-header/custom-column-header.component';
import { ProviderOrganizationsService } from 'src/app/services/provider.organizations.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggle } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-marketing-material-inventory-panel',
  templateUrl: './marketing-material-inventory-panel.component.html',
  styleUrls: ['./marketing-material-inventory-panel.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    AgGridWrapperComponent,
    SearchSortComponent,
    CustomColumnHeaderComponent,
    MatMenuModule,
    MatCheckboxModule,
    MatSlideToggle,
  ],
})
export class MarketingMaterialInventoryPanelComponent implements OnInit, OnDestroy {
  @Input() organizationId!: number;

  isLoading = false;
  datePipe = new DatePipe('en-US');

  private readonly destroy$ = new Subject<void>();
  private readonly reloadTrigger$ = new Subject<boolean>();

  pageSettings = {
    defaultPageSize: 20,
    pageSizeOptions: [20, 50, 100],
  };

  currentPage = 0;
  currentCursorId: number | null = null;
  currentPageSize = 20;
  pageSize = this.pageSettings.defaultPageSize;
  totalCount = 0;
  searchKey = '';

  searchControl: FormControl = new FormControl('');
  currentSortField = { sortValue: 'systemnumber' };
  currentIsAscending = true;

  sortForm: FormGroup = new FormGroup({
    sortField: new FormControl({ sortValue: this.currentSortField.sortValue }),
    isAscending: new FormControl(this.currentIsAscending),
  });

  sortOptions = [
    { label: 'System No', sortValue: 'systemnumber' },
    { label: 'Provider', sortValue: 'provider' },
    { label: 'Administrator', sortValue: 'administrator' },
    { label: 'Industry', sortValue: 'industry' },
  ];

  private readonly baseColumnDefs: ColDef[] = [
    {
      ...baseColDef,
      field: 'systemNumber',
      headerName: 'System No',
      width: 120,
      sortable: true,
      cellRenderer: TextTooltipRenderer,
      cellRendererParams: {
        onClick: this.viewDetails.bind(this),
      },
    },
    {
      ...baseColDef,
      field: 'revisionDate',
      headerName: 'Revision Date',
      width: 150,
      cellRenderer: (data: { value: string | null }) =>
        data.value ? new Date(data.value).toLocaleDateString() : '-',
    },
    {
      ...baseColDef,
      field: 'providerName',
      headerName: 'Provider',
      width: 150,
      sortable: true,
      cellRenderer: TextTooltipRenderer,
      valueGetter: (params: any) =>
        params.data?.provider?.name || params.data?.providerOrganization?.name || '-',
    },
    {
      ...baseColDef,
      field: 'administratorName',
      headerName: 'Administrator',
      width: 150,
      sortable: true,
      cellRenderer: TextTooltipRenderer,
      valueGetter: (params: any) => params.data?.administrator?.name || '-',
    },
    {
      ...baseColDef,
      field: 'industry',
      headerName: 'Industry',
      width: 150,
      sortable: false,
      cellRenderer: TextTooltipRenderer,
      valueGetter: (params: any) =>
        params.data?.industries?.map((i: any) => i.name).join(', ') || '-',
    },
    {
      ...baseColDef,
      field: 'products',
      headerName: 'Products',
      cellRenderer: MultiBadgeCellRenderer,
      width: 200,
      valueGetter: (params: any) => params.data?.products || [],
    },
    {
      ...baseColDef,
      field: 'states',
      headerName: 'States',
      cellRenderer: MultiBadgeCellRenderer,
      cellRendererParams: {
        maxItems: 3,
        separator: ' - ',
      },
      width: 200,
      valueGetter: (params: any) => params.data?.states || [],
    },
    {
      ...baseColDef,
      field: 'additionalApprovals',
      headerName: 'Additional Lender Approvals',
      cellRenderer: TextTooltipRenderer,
      width: 200,
      valueGetter: (params: any) => params.data?.additionalApprovals || '',
    },
    {
      ...baseColDef,
      field: 'documents',
      headerName: 'Marketing Material',
      width: 300,
      cellRenderer: GridDocumentComponent,
      valueGetter: (params: any) => {
        const documents = params.data?.documents || [];
        return documents.map((doc: any) => ({
          ...doc,
          serverFileName: doc.url || doc.serverFileName || doc.name,
        }));
      },
    },
    {
      ...baseColDef,
      field: 'isActive',
      headerName: 'Active/Inactive',
      width: 130,
      valueGetter: (params: any) =>
        params.data?.isActive
          ? { statusId: 5, name: 'Active' }
          : {
              statusId: 6,
              name: 'Inactive',
              dateMarkedInactive: params.data?.dateMarkedInactive,
              inactivatedByUser: params.data?.inactivatedByUser,
            },
      cellRenderer: FormStatusCellRenderer,
      comparator: (a, b) => (a?.statusId ?? 0) - (b?.statusId ?? 0),
    },
    {
      ...baseColDef,
      field: 'notes',
      headerName: 'Notes',
      width: 200,
      cellRenderer: TextTooltipRenderer,
      valueGetter: (params: any) => params.data?.notes || '',
    },
  ];

  public columnDefs: ColDef[] = [...this.baseColumnDefs];
  public rowData: any[] = [];
  gridApi!: GridApi;

  customColumns: CustomFieldDefinition[] = [];
  canAddColumns = false;

  private readonly ENTITY_TYPE = 'MarketingMaterialInventory';

  readonly userRole = toSignal(this.userService.userRole$, {
    initialValue: undefined,
  });
  readonly isAdmin = computed(() => this.userRole() === Role.Admin);
  readonly isProvider = computed(() => this.userRole() === Role.Provider);
  readonly isSystem = computed(() => this.userRole() === Role.SystemUser);
  readonly isConsultant = computed(() => this.userRole() === Role.Consultant);
  readonly isAdminOrSystem = computed(() => {
    const role = this.userRole();
    return role === Role.Admin || role === Role.SystemUser || role === Role.Provider;
  });
  readonly isLender = computed(() => this.userRole() === Role.Lender);
  readonly canCreate = computed(() => this.userRole() !== Role.Lender && this.userRole() !== Role.ProviderReadOnly && this.userRole() !== Role.LenderReadOnly);

  constructor(
    private readonly mmInventoryService: MarketingMaterialInventoryService,
    private readonly customFieldsService: CustomFieldDefinitionsService,
    private readonly deleteDialog: DeleteConfirmationDialogService,
    private readonly toastService: ToastService,
    private readonly userService: UsersService,
    private readonly providerOrgsService: ProviderOrganizationsService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.reloadTrigger$
      .pipe(
        switchMap((backward) => this.fetchData(backward)),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => {
        if (this.gridApi) this.gridApi.hideOverlay();
        this.rowData = data.items || [];
        this.totalCount = data.totalCount || 0;
        this.isLoading = false;
      });

    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentPage = 0;
        this.currentCursorId = null;
        this.reload();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.reload();
    this.loadCustomColumns();
  }

  // --- Custom column methods ---

  private loadCustomColumns(): void {
    const canPotentiallyAdd =
      this.isAdmin() || this.isProvider() || this.isConsultant();

    if (!canPotentiallyAdd) {
      this.canAddColumns = false;
      this.fetchAndRenderColumns();
      return;
    }

    this.providerOrgsService
      .getRelationships(this.organizationId.toString())
      .subscribe((response) => {
        const isChildOrg = !response.isError && response.data?.parent != null;
        this.canAddColumns = !isChildOrg;
        this.fetchAndRenderColumns();
      });
  }

  private fetchAndRenderColumns(): void {
    this.customFieldsService
      .getDefinitions(this.organizationId, this.ENTITY_TYPE)
      .subscribe((response) => {
        this.customColumns =
          !response.isError && response.data ? response.data : [];
        this.rebuildColumnDefs();
      });
  }

  onAddColumn(name: string, dataType: string, options?: any): void {
    if (this.customColumns.length >= 3) return;

    this.customFieldsService
      .createDefinition({
        orgId: this.organizationId,
        entityType: this.ENTITY_TYPE,
        fieldLabel: name,
        dataType: dataType || 'text',
        isRequired: options?.isRequired ?? false,
        isVisibleInGrid: options?.isVisibleInGrid ?? true,
        isVisibleInForm: options?.isVisibleInForm ?? true,
        sortOrder: this.customColumns.length,
      })
      .subscribe((response) => {
        if (!response.isError && response.data) {
          this.customColumns.push(response.data);
          this.rebuildColumnDefs();
        }
      });
  }

  toggleCustomColumnGrid(column: any): void {
    this.updateCustomColumnVisibility(column, {
      isVisibleInGrid: !column.isVisibleInGrid,
    });
  }

  toggleCustomColumnForm(column: any): void {
    this.updateCustomColumnVisibility(column, {
      isVisibleInForm: !column.isVisibleInForm,
    });
  }

  private updateCustomColumnVisibility(column: any, changes: any): void {
    this.customFieldsService
      .updateDefinition(column.id, { ...column, ...changes })
      .subscribe((response) => {
        if (!response.isError) {
          Object.assign(column, changes);
          this.rebuildColumnDefs();
        } else {
          this.toastService.error('Error', 'Failed to update custom column');
        }
      });
  }

  onRenameColumn(columnId: number, name: string): void {
    const col = this.customColumns.find((c) => c.id === columnId);
    if (!col) return;

    this.customFieldsService
      .updateDefinition(columnId, {
        fieldLabel: name,
        dataType: col.dataType,
        isRequired: col.isRequired,
        isVisibleInGrid: col.isVisibleInGrid,
        isVisibleInForm: col.isVisibleInForm,
        sortOrder: col.sortOrder,
      })
      .subscribe((response) => {
        if (!response.isError && response.data) {
          col.fieldLabel = response.data.fieldLabel;
          this.rebuildColumnDefs();
        } else {
          this.toastService.error('Error', 'Failed to rename column');
        }
      });
  }

  onDeleteColumn(columnId: number): void {
    this.deleteDialog
      .openDeleteConfirmation(
        'All existing values for this column will no longer be displayed.',
        'Delete column',
        'Delete',
      )
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.customFieldsService
          .deleteDefinition(columnId)
          .subscribe((response) => {
            if (!response.isError) {
              this.customColumns = this.customColumns.filter(
                (c) => c.id !== columnId,
              );
              this.rebuildColumnDefs();
            } else {
              this.toastService.error('Error', 'Failed to delete column');
            }
          });
      });
  }

  // --- Column building ---

  private buildCustomColDefs(): ColDef[] {
    const visibleCustomColumns = this.customColumns
      .filter((col) => col.isVisibleInGrid)
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const defs: ColDef[] = visibleCustomColumns.map((col) => ({
      ...baseColDef,
      field: `customFields.${col.fieldKey}`,
      width: 200,
      sortable: false,
      cellClass: 'custom-field-cell',
      headerComponent: CustomColumnHeaderComponent,
      headerComponentParams: {
        mode: 'rename',
        columnName: col.fieldLabel,
        columnId: col.id,
        canRename: this.canAddColumns,
        onRename: this.onRenameColumn.bind(this),
        onDelete: this.canAddColumns
          ? this.onDeleteColumn.bind(this)
          : undefined,
      },
      valueGetter: (params: any) =>
        params.data?.customFields?.[col.fieldKey] ?? '',
      valueFormatter: (params: any) => {
        if (col.dataType !== 'date' || !params.value) return params.value ?? '';
        return new Date(params.value).toLocaleDateString();
      },
    }));

    if (this.canAddColumns && this.customColumns.length < 3) {
      defs.push({
        field: 'customColAdd',
        headerName: '',
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        sortable: false,
        filter: false,
        resizable: false,
        cellClass: 'custom-field-cell',
        headerComponent: CustomColumnHeaderComponent,
        headerComponentParams: {
          mode: 'add',
          canAdd: true,
          onAdd: (name: string, dataType: string, options?: any) =>
            this.onAddColumn(name, dataType, options),
        },
      });
    }

    return defs;
  }

  private buildActionCol(): ColDef {
    return {
      field: 'action',
      minWidth: 160,
      maxWidth: 160,
      filter: false,
      sortable: false,
      cellRenderer: BtnCellRenderer,
      cellRendererParams: {
        onDelete: (data: any) => this.onDeleteMMI(data),
        onToggleStatus: this.isAdminOrSystem()
          ? (data: any) => this.onToggleActive(data)
          : null,
        showToggleStatus: this.isAdminOrSystem(),
      },
    };
  }

  private rebuildColumnDefs(): void {
    const allCols: ColDef[] = [
      ...this.baseColumnDefs,
      ...this.buildCustomColDefs(),
    ];
    if (!this.isLender() && this.userRole() !== Role.ProviderReadOnly && this.userRole() !== Role.LenderReadOnly) {
      allCols.push(this.buildActionCol());
    }
    this.columnDefs = allCols;
    this.gridApi.updateGridOptions({ columnDefs: allCols });
  }

  // --- Actions ---

  createNew(): void {
    const userRole = this.userRole();
    const base =
      userRole === Role.Admin || userRole === Role.SystemUser
        ? '/admin'
        : '/provider';
    this.router.navigate([`${base}/marketing-material-inventory/create`], {
      queryParams: { organizationId: this.organizationId },
    });
  }

  viewDetails(data: any): void {
    const userRole = this.userRole();
    const base =
      userRole === Role.Admin || userRole === Role.SystemUser
        ? '/admin'
        : (userRole === Role.Lender || userRole === Role.LenderReadOnly)
          ? '/lender'
          : '/provider';
    this.router.navigate(
      [`${base}/marketing-material-inventory/details/${data.id}`],
      { state: { organizationId: this.organizationId } },
    );
  }

  onDeleteMMI(data: any): void {
    this.deleteDialog
      .openDeleteConfirmation(
        'Are you sure you want to delete this Marketing Material?',
        'Delete confirmation',
        'Delete',
      )
      .afterClosed()
      .pipe(
        switchMap((confirmed) => {
          if (confirmed) return this.mmInventoryService.deleteMMI(data.id);
          return of(null);
        }),
      )
      .subscribe({
        next: (result: any) => {
          if (result && !result.isError) {
            this.toastService.success(
              'Success',
              'Marketing Material deleted successfully',
            );
            this.reload();
          }
        },
        error: () =>
          this.toastService.error('Error', 'Failed to delete Marketing Material'),
      });
  }

  onToggleActive(data: any): void {
    const newStatus = data.isActive ? 'Inactive' : 'Active';
    this.deleteDialog
      .openDeleteConfirmation(
        `Are you sure you want to change the status to ${newStatus}?`,
        'Toggle Status',
        'Confirm',
      )
      .afterClosed()
      .pipe(
        switchMap((confirmed) => {
          if (confirmed) return this.mmInventoryService.toggleActiveMMI(data.id);
          return of(null);
        }),
      )
      .subscribe({
        next: (result: any) => {
          if (result && !result.isError) {
            this.toastService.success('Success', `Status changed to ${newStatus}`);
            this.reload();
          }
        },
        error: () =>
          this.toastService.error('Error', 'Failed to toggle status'),
      });
  }

  // --- Search / Sort / Pagination ---

  setSortField(): void {
    const { sortField, isAscending } = this.sortForm.value;
    this.currentIsAscending = isAscending;
    this.currentSortField = sortField;
    this.currentCursorId = null;
    this.currentPage = 0;
    this.reload();
  }

  resetSortField(): void {
    this.sortForm.setValue({
      sortField: this.currentSortField,
      isAscending: this.currentIsAscending,
    });
  }

  handlePageEvent(event: PageEvent): void {
    if (this.isLoading) return;

    let backward = false;
    if (this.currentPageSize !== event.pageSize) {
      this.currentCursorId = null;
      this.currentPageSize = event.pageSize;
    } else if (
      !event.previousPageIndex ||
      event.previousPageIndex < event.pageIndex
    ) {
      this.currentCursorId =
        this.rowData.length > 0
          ? this.rowData[this.rowData.length - 1].id
          : null;
    } else {
      this.currentCursorId = this.rowData[0].id;
      backward = true;
    }
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.reload(backward);
  }

  private reload(backward = false): void {
    this.isLoading = true;
    this.searchKey = this.searchControl.value?.toLowerCase() || '';
    if (this.gridApi) this.gridApi.showLoadingOverlay();
    this.reloadTrigger$.next(backward);
  }

  private fetchData(backward: boolean): Observable<{ items: any[]; totalCount: number }> {
    let params: any = {
      providerOrganizationId: this.organizationId,
      sortColumn: this.currentSortField.sortValue,
      sortOrder: this.currentIsAscending ? 'asc' : 'desc',
      forward: !backward,
      pageSize: this.pageSize,
      searchKey: this.searchKey,
    };
    if (this.currentCursorId) {
      params = { ...params, lastId: this.currentCursorId };
    }
    return this.mmInventoryService
      .getAllMMI(params)
      .pipe(
        map((response: any) =>
          response.isError ? { items: [], totalCount: 0 } : response.data,
        ),
      );
  }
}
